<template>
  <!-- 异常详情弹框 -->
  <el-dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    width="680px"
    top="8vh"
    :close-on-click-modal="false"
    append-to-body
    custom-class="anomaly-detail-dialog"
  >
    <!-- 标题栏 -->
    <div slot="title" class="anomaly-dialog-title">
      <div class="title-main">
        <i class="el-icon-warning title-icon"></i>
        <span>异常报告详情</span>
      </div>
      <div class="title-sub" v-if="detail">
        <span class="report-title">{{ detail.title || '—' }}</span>
        <span class="risk-tag" :class="riskTagClass">{{ riskLabel }}</span>
      </div>
    </div>

    <!-- 加载骨架 -->
    <div v-if="loading" class="anomaly-loading">
      <i class="el-icon-loading"></i> 加载中...
    </div>

    <!-- 主体内容 -->
    <div v-else-if="detail" class="anomaly-body">
      <!-- Markdown 渲染区域：异常内容优先，没有则回退到 content。挂 markdown-body 类以复用全局 report-markdown.scss -->
      <div class="anomaly-md markdown-body" v-html="renderedContent"></div>
    </div>

    <!-- 无数据 -->
    <div v-else class="anomaly-loading">
      未找到该异常报告详情
    </div>

    <!-- 底部：处置区 + 按钮 -->
    <div slot="footer" class="anomaly-dialog-footer">
      <!-- 处置区 -->
      <div v-if="detail && !loading" class="anomaly-footer">
        <!-- 已处置态：只读回显，颜色随状态区分：完成=绿 / 暂缓=黄 / 误报=红 -->
        <div v-if="dispositionDone" class="disposition-done" :class="dispositionStatusClass">
          <i :class="dispositionIcon"></i>
          <span class="disposition-label">处置结果</span>
          <span class="disposition-status" :class="dispositionStatusClass">{{ dispositionStatusLabel }}</span>
          <span v-if="detail.anomalyDispositionTime" class="disposition-time">
            · {{ formatTime(detail.anomalyDispositionTime) }}
          </span>
          <p v-if="detail.anomalyDispositionNote" class="disposition-note">
            {{ detail.anomalyDispositionNote }}
          </p>
        </div>

        <!-- 待处置态：处置表单 -->
        <div v-else class="disposition-form">
          <div class="disposition-row">
            <span class="disposition-title">
              异常处置
            </span>
            <el-radio-group v-model="formStatus" size="small">
              <el-radio label="DONE">已完成</el-radio>
              <el-radio label="FALSE_POSITIVE">误报</el-radio>
              <el-radio label="DEFERRED">暂缓</el-radio>
            </el-radio-group>
          </div>
          <el-input
            v-model="formNote"
            type="textarea"
            :rows="2"
            placeholder="填写处置说明（选填）..."
            maxlength="200"
            show-word-limit
          ></el-input>
        </div>
      </div>
      <!-- 按钮区 -->
      <div class="footer-actions">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button
          v-if="detail && !dispositionDone"
          size="small"
          type="primary"
          :loading="submitting"
          @click="handleSubmit"
        >
          提交处置
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getJobReportDetailApi, disposeAnomalyApi } from '@/api/agentAuth'
import { renderMarkdownText } from '@/utils/markdownRenderer'

const STATUS_LABEL_MAP = {
  DONE: '已完成',
  FALSE_POSITIVE: '误报',
  DEFERRED: '暂缓'
}

export default {
  name: 'AnomalyDetailDialog',
  props: {
    visible: { type: Boolean, default: false },
    /** 报告对象，至少要带 id */
    report: { type: Object, default: () => ({}) },
    agentId: { type: [String, Number], default: '' }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      detail: null,
      formStatus: 'DONE',
      formNote: ''
    }
  },
  computed: {
    /** 渲染的 markdown 内容：优先 anomalyContent（异常报告内容），没有则回退到 content（整体报告） */
    renderedContent() {
      if (!this.detail) return ''
      const md = this.detail.anomalyContent || this.detail.content || ''
      return renderMarkdownText(md)
    },
    /** 是否已经处置过 */
    dispositionDone() {
      return !!(this.detail && this.detail.anomalyDispositionStatus)
    },
    dispositionStatusLabel() {
      if (!this.detail) return ''
      return STATUS_LABEL_MAP[this.detail.anomalyDispositionStatus] || '已处置'
    },
    /** 处置状态对应的样式 class：done=绿 / deferred=黄 / false-positive=红 */
    dispositionStatusClass() {
      const status = this.detail && this.detail.anomalyDispositionStatus
      if (status === 'FALSE_POSITIVE') return 'false-positive'
      if (status === 'DEFERRED') return 'deferred'
      return 'done'
    },
    /** 处置状态对应的图标：完成=success / 暂缓=warning / 误报=circle-close */
    dispositionIcon() {
      const status = this.detail && this.detail.anomalyDispositionStatus
      if (status === 'FALSE_POSITIVE') return 'el-icon-circle-close'
      if (status === 'DEFERRED') return 'el-icon-warning'
      return 'el-icon-success'
    },
    /** 风险标签 */
    riskLabel() {
      if (!this.detail) return ''
      const map = { 1: '重大影响', 2: '中度影响', 3: '常规监测' }
      return map[this.detail.impactLevel] || '常规监测'
    },
    riskTagClass() {
      const level = this.detail && this.detail.impactLevel
      if (level === 1) return 'risk-tag-high'
      if (level === 2) return 'risk-tag-mid'
      return 'risk-tag-low'
    }
  },
  watch: {
    visible(val) {
      if (val && this.report && this.report.id) {
        this.loadDetail()
      } else if (!val) {
        // 关闭时清理
        this.detail = null
        this.formStatus = 'DONE'
        this.formNote = ''
      }
    }
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const res = await getJobReportDetailApi(this.report.id)
        const data = res && res.data ? res.data : res
        this.detail = data || null
        // 已有处置结果时回显
        if (this.detail && this.detail.anomalyDispositionStatus) {
          this.formStatus = this.detail.anomalyDispositionStatus
          this.formNote = this.detail.anomalyDispositionNote || ''
        }
      } catch (e) {
        console.error('[AnomalyDetailDialog] 加载异常报告详情失败:', e)
        this.$message.error('加载异常报告详情失败')
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      if (!this.detail) return
      if (!this.formStatus) {
        this.$message.warning('请选择处置结论')
        return
      }
      this.submitting = true
      try {
        await disposeAnomalyApi(this.detail.id, {
          status: this.formStatus,
          note: this.formNote
        })
        this.$message.success('处置提交成功')
        // 重新拉取详情以回显最新状态
        await this.loadDetail()
        // 通知父组件
        this.$emit('disposed', this.detail)
      } catch (e) {
        console.error('[AnomalyDetailDialog] 处置失败:', e)
        this.$message.error('处置提交失败')
      } finally {
        this.submitting = false
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    formatTime(time) {
      if (!time) return ''
      try {
        const d = new Date(time)
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch {
        return String(time)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.anomaly-detail-dialog {
  .anomaly-dialog-title {
    .title-main {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #1f2329;
      margin-bottom: 6px;

      .title-icon {
        color: #ef4444;
        font-size: 18px;
      }
    }

    .title-sub {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 12px;
      font-weight: normal;

      .report-title {
        color: #4b5563;
      }

      .risk-tag {
        font-size: 11px;
        padding: 1px 8px;
        border-radius: 10px;

        &.risk-tag-high {
          background: #fef2f2;
          color: #ef4444;
          border: 1px solid #fecaca;
        }

        &.risk-tag-mid {
          background: #fff7ed;
          color: #ea580c;
          border: 1px solid #fed7aa;
        }

        &.risk-tag-low {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
      }
    }
  }

  .anomaly-loading {
    text-align: center;
    padding: 40px;
    color: #94a3b8;
  }

  .anomaly-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .anomaly-md {
    padding: 4px 4px 12px;
    // markdown 元素样式（h1-h6/p/ul/ol/li/table/th/td/code/pre/blockquote/checkbox 等）
    // 由全局 report-markdown.scss 通过 .markdown-body 类接管，与经营分析/市场洞察详情页保持一致
  }

  // 处置区（在 footer 内）
  .anomaly-footer {
    margin-bottom: 10px;
  }

  // 底部按钮区
  .footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  // 已处置回显（颜色随状态区分：完成=绿 / 暂缓=黄 / 误报=红）
  .disposition-done {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 13px;
    // 默认变体 = 完成（绿）
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #15803d;

    // 暂缓 = 黄
    &.deferred {
      background: #fefce8;
      border-color: #fde68a;
      color: #854d0e;
    }

    // 误报 = 红
    &.false-positive {
      background: #fef2f2;
      border-color: #fecaca;
      color: #b91c1c;
    }

    i {
      font-size: 16px;
    }

    .disposition-label {
      font-weight: 600;
      color: inherit; // 跟随父级状态色
    }

    .disposition-status {
      font-weight: 600;
      padding: 1px 8px;
      border-radius: 10px;
      color: #fff;

      &.done { background: #16a34a; }
      &.deferred { background: #ca8a04; }
      &.false-positive { background: #dc2626; }
    }

    .disposition-time {
      font-size: 12px;
      color: #64748b; // 时间保持中性灰
    }

    .disposition-note {
      width: 100%;
      margin: 0;
      padding: 6px 0 0 26px;
      font-size: 12px;
      color: #4b5563; // 说明文字保持中性
      font-weight: normal;
    }
  }

  .disposition-form {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .disposition-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .disposition-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #1f2329;
      flex-shrink: 0;

      i {
        color: #ef4444;
      }
    }
  }
}
</style>

<!-- 全局样式：el-dialog append-to-body 后 scoped 不作用于 dialog 容器，需用全局样式 -->
<style lang="scss">
.anomaly-detail-dialog {
  margin-top: 8vh !important;
  margin-bottom: 8vh !important;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 16vh);

  .el-dialog__header {
    flex-shrink: 0;
    padding: 16px 20px 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  }
  .el-dialog__body {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding: 16px 20px;
  }
  .el-dialog__footer {
    flex-shrink: 0;
    padding: 12px 20px;
    border-top: 1px solid #f0f0f0;
  }
}

/* 复用经营分析/市场洞察详情页的全局 Markdown 样式（含表格斑马纹、横向滚动、CSS 变量等） */
@import '@/assets/styles/report-markdown.scss';
</style>
